import { Link } from 'react-router-dom'
import { useConfig } from '../hooks/useConfig'

const UPDATED = '13 October 2026'
const CONTACT = 'brian@bmurray.ca'

function useName() {
  return useConfig().data?.libraryName || 'The Library of Alexandria'
}

/** Terms of service for the demo library. Plain language on purpose. */
export function Terms() {
  const name = useName()
  return (
    <article className="page-section narrow legal">
      <p className="eyebrow">Terms of service</p>
      <h1 className="display">Borrowing from {name}</h1>
      <p className="muted">Last updated {UPDATED}.</p>

      <h2>What this is</h2>
      <p>
        {name} is a demonstration library management system built as a coding exercise. It is run by a single
        person as a personal project. It is provided as-is, free of charge, with no promise that it stays online,
        keeps your data, or behaves correctly. Please do not rely on it for anything that matters.
      </p>

      <h2>Your account</h2>
      <p>
        You sign in with a Google account. You are responsible for what is done while signed in as you. New
        accounts start as members; librarians and administrators are assigned by the site owner. The owner may
        change roles, remove accounts, or reset the catalog at any time.
      </p>

      <h2>Acceptable use</h2>
      <ul>
        <li>Use the site as a library: browse, search, borrow, return, place holds, ask the librarian.</li>
        <li>Do not try to break it, probe it, scrape it, or overload it.</li>
        <li>Do not add content that is unlawful, abusive, or that you do not have the right to share.</li>
        <li>Do not send the AI librarian anything private, sensitive, or that you would not say out loud.</li>
      </ul>

      <h2>The AI librarian</h2>
      <p>
        Chat replies are generated by a third-party language model. They can be wrong, and they are not advice of
        any kind. The catalog tools the model uses are the same ones you can use yourself; the model cannot do
        anything you could not.
      </p>

      <h2>Books and content</h2>
      <p>
        The catalog is a sample of well-known titles with descriptions written for this demo and covers served by
        Open Library. There are no physical books and no real loans, only records of pretend ones.
      </p>

      <h2>Liability</h2>
      <p>
        To the fullest extent permitted by law, the site owner is not liable for any loss or damage arising from
        your use of the site, its unavailability, or errors in its content.
      </p>

      <h2>Changes</h2>
      <p>
        These terms may change. The date at the top is the version you are reading. Continued use after a change
        means you accept the new terms.
      </p>

      <h2>Contact</h2>
      <p>
        Questions go to <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. See also the <Link to="/privacy">privacy policy</Link>.
      </p>
    </article>
  )
}

/** Privacy policy: exactly what is collected, why, where it goes, how to remove it. */
export function Privacy() {
  const name = useName()
  return (
    <article className="page-section narrow legal">
      <p className="eyebrow">Privacy policy</p>
      <h1 className="display">What {name} keeps about you</h1>
      <p className="muted">Last updated {UPDATED}.</p>

      <h2>In short</h2>
      <p>
        The site stores the minimum it needs to run a library for you: who you are, what you borrowed, and what you
        asked the librarian. Nothing is sold, shared for advertising, or used to train anything.
      </p>

      <h2>What is collected</h2>
      <ul>
        <li>
          <strong>From Google sign-in:</strong> your email address, display name, profile picture URL and Google
          account id. Only the <code>openid</code>, <code>email</code> and <code>profile</code> scopes are requested;
          the site never sees your password and cannot act on your Google account.
        </li>
        <li>
          <strong>Library activity:</strong> books you check out and return (with dates), holds you place, and, for
          librarians, catalog changes you make.
        </li>
        <li>
          <strong>Librarian chat:</strong> the messages you send to the AI librarian and its replies, kept so you
          can continue a conversation. Anonymous visitors get a random cookie so their conversation can be found
          again; it contains no personal information.
        </li>
        <li>
          <strong>Technical logs:</strong> request paths, status codes, timings and the client IP address, kept for
          a short period for debugging and abuse prevention.
        </li>
      </ul>

      <h2>Cookies</h2>
      <p>
        Two cookies, both first-party and both essential: a session cookie after you sign in, and an anonymous
        conversation cookie if you use the librarian without signing in. There are no analytics or advertising
        cookies.
      </p>

      <h2>Where your data goes</h2>
      <ul>
        <li>
          <strong>OpenAI</strong> receives the text of your librarian conversation and the catalog information the
          model looks up, so it can reply. It does not receive your email or name beyond what you type.
        </li>
        <li>
          <strong>Google</strong> handles sign-in under its own privacy policy.
        </li>
        <li>
          <strong>Open Library</strong> serves book cover images; your browser requests them directly.
        </li>
        <li>
          Everything else lives in a single database on a server operated by the site owner in Canada.
        </li>
      </ul>

      <h2>How long it is kept</h2>
      <p>
        Account and loan records stay until you ask for them to be removed or the demo is reset. Sessions expire
        after 30 days. Conversations can be deleted by you at any time from the librarian panel.
      </p>

      <h2>Your choices</h2>
      <p>
        You can delete your conversations yourself. To have your account and its history removed, email{' '}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a> from the address you signed in with and it will be done promptly.
        You can also revoke the site's access from your Google account settings at any time.
      </p>

      <h2>Children</h2>
      <p>This is a demonstration for adults evaluating software. It is not directed at children.</p>

      <h2>Changes and contact</h2>
      <p>
        This policy may change; the date above is the version you are reading. Questions go to{' '}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. See also the <Link to="/tos">terms of service</Link>.
      </p>
    </article>
  )
}
